import React, { useRef, useEffect, useState } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'

const FACE_NORMALS = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
]

const COLOR_PALETTE = [
  '#01303f',
  '#02577a',
  '#02a9f7',
  '#89d6fb',
  '#d4f0fc',
]

const N = 10
const TOTAL_CUBES = 6 * N * N

function faceToWorldPosition(normal: THREE.Vector3, u: number, v: number) {
  const pos = new THREE.Vector3()
  if (normal.x !== 0) {
    pos.set(normal.x, u, v)
  } else if (normal.y !== 0) {
    pos.set(u, normal.y, v)
  } else {
    pos.set(u, v, normal.z)
  }
  return pos
}

export default function BumpyCube({ position = [0, 0, 0] }) {
  const groupRef = useRef<THREE.Group>(null)
  const instancedMeshRef = useRef<THREE.InstancedMesh>(null)


const [rotationAxis] = useState(() => {
  return new THREE.Vector3(
    Math.random() - 0.5,
    Math.random() - 0.5,
    Math.random() - 0.5
  ).normalize()
})
  const [rotationSpeed] = useState(() => 0.005 + Math.random() * 0.01)

  const [cubeGeometry] = useState(
    () => new THREE.BoxGeometry(2 / N, 2 / N, 0.2)
  )

  const colorArray = useRef(new Float32Array(TOTAL_CUBES * 3))

  useEffect(() => {

    if (!instancedMeshRef.current) return

    let i = 0
    const dummyMatrix = new THREE.Matrix4()
    const dummyColor = new THREE.Color()

    FACE_NORMALS.forEach(normal => {
      for (let row = 0; row < N; row++) {
        for (let col = 0; col < N; col++) {
          const u = -1 + ((col + 0.5) / N) * 2
          const v = -1 + ((row + 0.5) / N) * 2

          const pos = faceToWorldPosition(normal, u, v)
          const depthOffset = (Math.random() - 0.5) * 0.2
          pos.addScaledVector(normal, depthOffset)

          dummyMatrix.makeTranslation(pos.x, pos.y, pos.z)
          instancedMeshRef.current.setMatrixAt(i, dummyMatrix)

          const randomIndex = Math.floor(Math.random() * COLOR_PALETTE.length)
          dummyColor.set(COLOR_PALETTE[randomIndex])
          dummyColor.toArray(colorArray.current, i * 3)

          i++
        }
      }
    })


    instancedMeshRef.current.geometry.setAttribute(
      'color',
      new THREE.InstancedBufferAttribute(colorArray.current, 3)
    )
    instancedMeshRef.current.instanceMatrix.needsUpdate = true
  }, [])

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.rotateOnAxis(rotationAxis, rotationSpeed)
    }
  })

  return (
    <group ref={groupRef} position={position}>
      <instancedMesh
        ref={instancedMeshRef}
        args={[cubeGeometry, null, TOTAL_CUBES]}
      >
        <meshStandardMaterial
          vertexColors
          metalness={0.3}
          roughness={0.6}
          emissive="#333333"
          emissiveIntensity={0.5}
        />
        <ambientLight intensity={0.6} />
        <pointLight position={[10, 10, 10]} intensity={1.2} />
      </instancedMesh>
    </group>
  )
}
